import { FormEvent, useEffect, useMemo, useState } from 'react';
import './App.css';
import { homeService, type DashboardSummary, type QuickStats, type WeatherCard } from './modules/home/service';
import { pbClient } from './services/pbClient';
import { runtimeEnv } from './services/runtimeEnv';
import { useAppSession } from './hooks/useAppSession';
import { useOfflineQueueStatus } from './hooks/useOfflineQueueStatus';

type HealthState = 'checking' | 'online' | 'offline';

function App() {
  const [activeSection, setActiveSection] = useState<'dashboard' | 'events' | 'study' | 'workout'>('dashboard');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [health, setHealth] = useState<HealthState>('checking');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isBusy, setIsBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [dashboardWarnings, setDashboardWarnings] = useState<string[]>([]);
  const [actionNotice, setActionNotice] = useState('');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [weather, setWeather] = useState<WeatherCard | null>(null);
  const env = useMemo(() => runtimeEnv.getEnvironment(), []);
  const { isAuthed, authUser, sessionNotice, clearSessionNotice } = useAppSession();
  const { queueCount } = useOfflineQueueStatus();

  useEffect(() => {
    const loadDashboard = async () => {
      if (!isAuthed || !authUser?.id) {
        setSummary(null);
        setStats(null);
        setWeather(null);
        setDashboardError('');
        setDashboardWarnings([]);
        return;
      }

      setDashboardLoading(true);
      setDashboardError('');
      setDashboardWarnings([]);

      try {
        const [summaryResult, statsResult, weatherResult] = await Promise.allSettled([
          homeService.getDashboardSummary(),
          homeService.getQuickStats(),
          homeService.getWeather(),
        ]);

        const warnings: string[] = [];

        if (summaryResult.status === 'fulfilled') {
          setSummary(summaryResult.value);
        } else {
          setSummary(null);
          const reason = summaryResult.reason instanceof Error ? summaryResult.reason.message : 'unknown error';
          warnings.push(`Summary unavailable: ${reason}`);
        }

        if (statsResult.status === 'fulfilled') {
          setStats(statsResult.value);
        } else {
          setStats(null);
          const reason = statsResult.reason instanceof Error ? statsResult.reason.message : 'unknown error';
          warnings.push(`Quick stats unavailable: ${reason}`);
        }

        if (weatherResult.status === 'fulfilled') {
          setWeather(weatherResult.value);
        } else {
          setWeather(null);
          const reason = weatherResult.reason instanceof Error ? weatherResult.reason.message : 'unknown error';
          warnings.push(`Weather unavailable: ${reason}`);
        }

        setDashboardWarnings(warnings);

        if (warnings.length === 3) {
          setDashboardError('Dashboard services are currently unavailable. Please try again.');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load dashboard';
        setDashboardError(message);
      } finally {
        setDashboardLoading(false);
      }
    };

    loadDashboard();
  }, [isAuthed, authUser?.id, tick]);

  useEffect(() => {
    const checkHealth = async () => {
      setHealth('checking');
      try {
        const res = await fetch(`${env.pbUrl}/api/health`, { method: 'GET' });
        setHealth(res.ok ? 'online' : 'offline');
      } catch {
        setHealth('offline');
      }
    };

    checkHealth();
  }, [env.pbUrl, tick]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError('');
    clearSessionNotice();
    setIsBusy(true);

    try {
      await pbClient.authWithPassword(email.trim(), password);
      setTick((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setAuthError(message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleLogout = async () => {
    setIsBusy(true);
    setAuthError('');
    clearSessionNotice();
    try {
      await pbClient.signOut();
      setTick((prev) => prev + 1);
    } finally {
      setIsBusy(false);
    }
  };

  const requireAuthForAction = () => {
    if (!isAuthed || !authUser?.id) {
      setActionNotice('Please sign in first.');
      return false;
    }
    return true;
  };

  const handleCreateCalendarEvent = async () => {
    if (!requireAuthForAction()) return;
    setActionNotice('');
    setIsBusy(true);
    try {
      const now = new Date();
      const end = new Date(now.getTime() + 60 * 60 * 1000);
      await homeService.createQuickCalendarEvent({
        title: 'Quick event',
        startDate: now.toISOString(),
        endDate: end.toISOString(),
        description: 'Created from dashboard quick action',
      });
      setActionNotice('Calendar event created.');
      setTick((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create calendar event';
      setActionNotice(message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleCreateStudyTask = async () => {
    if (!requireAuthForAction()) return;
    setActionNotice('');
    setIsBusy(true);
    try {
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      await homeService.createQuickStudyTask({
        title: 'Quick study task',
        subject: 'General',
        dueDate: tomorrow,
        priority: 'medium',
      });
      setActionNotice('Study task created.');
      setTick((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create study task';
      setActionNotice(message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleLogWorkout = async () => {
    if (!requireAuthForAction()) return;
    setActionNotice('');
    setIsBusy(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await homeService.createQuickWorkout({
        date: today,
        exerciseType: 'Quick workout',
        duration: 30,
        intensity: 'medium',
      });
      setActionNotice('Workout logged.');
      setTick((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to log workout';
      setActionNotice(message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleCreateFlashcardDeck = async () => {
    if (!requireAuthForAction()) return;
    setActionNotice('');
    setIsBusy(true);
    try {
      await homeService.createQuickDeck({
        name: `Quick Deck ${new Date().toLocaleTimeString()}`,
        description: 'Created from dashboard quick action',
      });
      setActionNotice('Flashcard deck created.');
      setTick((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create flashcard deck';
      setActionNotice(message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleSectionChange = (section: 'dashboard' | 'events' | 'study' | 'workout') => {
    setActiveSection(section);
    if (section !== 'dashboard') {
      setActionNotice(`Section ${section} is active. Full module page wiring is being integrated next.`);
    } else {
      setActionNotice('');
    }
  };

  return (
    <div className="clarix-app" data-testid="app-ready">
      <header className="topbar">
        <div className="brand">Clarix</div>
        <div className="topbar-right">
          <div className={`pill ${health === 'online' ? 'pill-ok' : health === 'offline' ? 'pill-error' : ''}`}>
            Health: <span data-testid="pb-health" data-state={health}>{health}</span>
          </div>
          <div className={`pill ${isOnline ? 'pill-ok' : 'pill-error'}`}>
            Network: <span data-testid="network-state">{isOnline ? 'online' : 'offline'}</span>
          </div>
          {isAuthed ? (
            <button data-testid="logout-button" type="button" className="btn btn-danger" onClick={handleLogout} disabled={isBusy}>
              {isBusy ? 'Signing out...' : 'Logout'}
            </button>
          ) : null}
        </div>
      </header>

      <div className="layout">
        {isAuthed ? (
          <aside className="sidebar">
            <p className="sidebar-title">Main System</p>
            <nav className="sidebar-nav">
              <button type="button" className={activeSection === 'dashboard' ? 'nav-active' : ''} onClick={() => handleSectionChange('dashboard')}>Dashboard</button>
              <button type="button" className={activeSection === 'events' ? 'nav-active' : ''} onClick={() => handleSectionChange('events')}>Events</button>
              <button type="button" className={activeSection === 'study' ? 'nav-active' : ''} onClick={() => handleSectionChange('study')}>Study</button>
              <button type="button" className={activeSection === 'workout' ? 'nav-active' : ''} onClick={() => handleSectionChange('workout')}>Workout</button>
            </nav>
          </aside>
        ) : null}

        <main className="canvas">
          <p data-testid="auth-state" className="visually-hidden">
            {isAuthed ? 'authenticated' : 'unauthenticated'}
          </p>

          {!isAuthed ? (
            <section className="login-shell">
              <div className="login-card">
                <div className="login-head">
                  <p className="eyebrow">Clarix</p>
                  <h1>Welcome Back</h1>
                  <p>Enter your credentials to access your workspace.</p>
                </div>

                <form className="auth-form" onSubmit={handleLogin} data-testid="login-form">
                  <label>
                    Email
                    <input
                      data-testid="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="architect@clarix.sys"
                      required
                    />
                  </label>
                  <label>
                    Password
                    <input
                      data-testid="login-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </label>
                  {authError ? (
                    <p className="alert alert-error" data-testid="auth-error">
                      {authError}
                    </p>
                  ) : null}
                  {sessionNotice ? (
                    <p className="alert alert-error" data-testid="session-notice">
                      {sessionNotice}
                    </p>
                  ) : null}
                  <button data-testid="login-button" className="btn btn-primary" type="submit" disabled={isBusy}>
                    {isBusy ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>

                <div className="login-meta">
                  <span>Queue Items</span>
                  <strong data-testid="queue-count">{queueCount}</strong>
                </div>
              </div>
            </section>
          ) : (
            <>
              <section className="hero-panel">
                <div>
                  <p className="eyebrow">Daily Productivity Pulse</p>
                  <h1>Focus Peak</h1>
                  <p className="hero-subline">Signed in as <strong data-testid="auth-session">{authUser?.email || authUser?.id}</strong></p>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => setTick((prev) => prev + 1)}>
                  Refresh Dashboard
                </button>
              </section>

              {dashboardWarnings.length > 0 ? (
                <div className="alert alert-warning" data-testid="dashboard-warning">
                  Loaded with partial data: {dashboardWarnings.join(' | ')}
                </div>
              ) : null}

              {dashboardError ? <div className="alert alert-error">{dashboardError}</div> : null}

              <section className="cards-grid">
                <article className="panel">
                  <h2>System Signals</h2>
                  <dl className="stats">
                    <div>
                      <dt>Queue Items</dt>
                      <dd data-testid="queue-count">{queueCount}</dd>
                    </div>
                    <div>
                      <dt>Environment</dt>
                      <dd>{env.appEnv}</dd>
                    </div>
                  </dl>
                </article>

                <article className="panel panel-weather">
                  <h2>Weather</h2>
                  {dashboardLoading ? (
                    <p className="muted">Syncing atmospheric data...</p>
                  ) : (
                    <>
                      <p className="metric-value">{weather?.temp ?? 0}°C</p>
                      <p className="metric-caption">{weather?.condition ?? 'Unknown'}</p>
                      <p className="muted">{weather?.message ?? 'Weather integration coming soon'}</p>
                    </>
                  )}
                </article>
              </section>

              {activeSection === 'dashboard' ? (
                <article className="panel panel-wide">
                  <h2>Home Overview</h2>
                  {!dashboardError && dashboardLoading ? (
                    <p className="muted">Loading your dashboard...</p>
                  ) : (
                    <div className="overview-grid">
                      <section className="metric-card">
                        <h3>Events</h3>
                        <p className="metric-value">{summary?.upcomingEventsCount ?? 0}</p>
                        <p className="metric-caption">Upcoming events</p>
                      </section>
                      <section className="metric-card">
                        <h3>Study</h3>
                        <p className="metric-value">{summary?.pendingHomeworkCount ?? 0}</p>
                        <p className="metric-caption">Pending assignments</p>
                      </section>
                      <section className="metric-card">
                        <h3>Fitness</h3>
                        <p className="metric-value">{stats?.monthlyExerciseMinutes ?? 0}</p>
                        <p className="metric-caption">Minutes this month</p>
                      </section>
                      <section className="metric-card">
                        <h3>Workouts</h3>
                        <p className="metric-value">{summary?.recentWorkoutsCount ?? 0}</p>
                        <p className="metric-caption">Recent sessions</p>
                      </section>
                    </div>
                  )}
                </article>
              ) : null}

              {activeSection === 'events' ? (
                <article className="panel panel-wide">
                  <h2>Events</h2>
                  <div className="overview-grid section-grid">
                    <section className="metric-card">
                      <h3>Upcoming</h3>
                      <p className="metric-value">{summary?.upcomingEventsCount ?? 0}</p>
                      <p className="metric-caption">Events in queue</p>
                    </section>
                    <section className="metric-card action-card">
                      <h3>Create</h3>
                      <p className="metric-caption">Fast event creation from this panel.</p>
                      <button type="button" className="btn btn-primary" onClick={handleCreateCalendarEvent} disabled={isBusy}>Create Event</button>
                    </section>
                  </div>
                </article>
              ) : null}

              {activeSection === 'study' ? (
                <article className="panel panel-wide">
                  <h2>Study</h2>
                  <div className="overview-grid section-grid">
                    <section className="metric-card">
                      <h3>Pending</h3>
                      <p className="metric-value">{summary?.pendingHomeworkCount ?? 0}</p>
                      <p className="metric-caption">Assignments pending</p>
                    </section>
                    <section className="metric-card action-card">
                      <h3>Create</h3>
                      <p className="metric-caption">Add a study task with one click.</p>
                      <button type="button" className="btn btn-primary" onClick={handleCreateStudyTask} disabled={isBusy}>Add Study Task</button>
                    </section>
                  </div>
                </article>
              ) : null}

              {activeSection === 'workout' ? (
                <article className="panel panel-wide">
                  <h2>Workout</h2>
                  <div className="overview-grid section-grid">
                    <section className="metric-card">
                      <h3>Minutes</h3>
                      <p className="metric-value">{stats?.monthlyExerciseMinutes ?? 0}</p>
                      <p className="metric-caption">This month</p>
                    </section>
                    <section className="metric-card action-card">
                      <h3>Log</h3>
                      <p className="metric-caption">Track a workout from this panel.</p>
                      <button type="button" className="btn btn-primary" onClick={handleLogWorkout} disabled={isBusy}>Log Workout</button>
                    </section>
                  </div>
                </article>
              ) : null}

              <article className="panel panel-wide">
                <h2>Quick Actions</h2>
                <div className="quick-actions">
                  <button type="button" className="action action-primary" onClick={handleCreateCalendarEvent} disabled={isBusy}>Create Event</button>
                  <button type="button" className="action" onClick={handleCreateStudyTask} disabled={isBusy}>Add Study Task</button>
                  <button type="button" className="action" onClick={handleLogWorkout} disabled={isBusy}>Log Workout</button>
                  <button type="button" className="action" onClick={handleCreateFlashcardDeck} disabled={isBusy}>New Flashcards</button>
                </div>
                {actionNotice ? (
                  <p className={actionNotice.toLowerCase().includes('failed') || actionNotice.toLowerCase().includes('please sign in') ? 'alert alert-error' : 'alert alert-success'}>
                    {actionNotice}
                  </p>
                ) : null}
              </article>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
